import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronRight, Plus, Trash2, Users, UsersRound } from 'lucide-react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@reflex/ui/components/ui/alert-dialog';
import { CanGuard } from '@reflex/ui/client/permissions';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { api } from '@/lib/api';
import { mutateWithToast } from '@/lib/mutation-helpers';
import { useRefreshSession } from '@/hooks/auth/useSessionRefresh';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { OrganizationTeamDetailPage } from './OrganizationTeamDetailPage';

const createTeamSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]*$/, 'Lowercase letters, numbers, and dashes only')
    .max(60)
    .optional()
    .or(z.literal('')),
  description: z.string().max(500).optional().or(z.literal('')),
});

type CreateTeamFormValues = z.infer<typeof createTeamSchema>;

/**
 * Teams list, with team detail rendered in place when the URL carries
 * `/teams/:teamId` (Back chip returns to the list without leaving Teams).
 * Nested team secrets / model-providers stay on their own routes.
 */
export function OrganizationTeamsPage() {
  const { teamId } = useParams<{ teamId?: string }>();
  if (teamId) {
    return (
      <HubReadGuard permission="teams:read" team={teamId}>
        <OrganizationTeamDetailPage />
      </HubReadGuard>
    );
  }

  return (
    <HubReadGuard permission="teams:read">
      <RequireActiveOrganization title="Teams">
        {(org) => <TeamsBody key={org.id} orgId={org.id} />}
      </RequireActiveOrganization>
    </HubReadGuard>
  );
}

function TeamsBody({ orgId }: { orgId: string }) {
  const routes = useOrgRoutes();
  const queryClient = useQueryClient();
  const refreshSession = useRefreshSession();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const teamsQuery = useQuery({
    queryKey: ['organizations', orgId, 'teams'],
    queryFn: () => api.listTeams(orgId),
    enabled: !!orgId,
  });

  const invalidateTeams = () => {
    queryClient.invalidateQueries({ queryKey: ['organizations', orgId, 'teams'] });
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateTeamFormValues>({
    resolver: zodResolver(createTeamSchema),
    mode: 'onChange',
    defaultValues: { name: '', slug: '', description: '' },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; slug?: string; description?: string | null }) =>
      api.createTeam(orgId, data),
    onSuccess: () => {
      invalidateTeams();
      // `POST /organizations/:id/teams` is "create a team and join it as its
      // maintainer", so this changes the caller's own team list.
      refreshSession();
      setIsCreateOpen(false);
      reset();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (teamId: string) => api.deleteTeam(teamId),
    onSuccess: () => {
      invalidateTeams();
      // Deleting a team the caller is on drops it from their membership.
      refreshSession();
    },
  });

  function handleOpenChange(next: boolean) {
    setIsCreateOpen(next);
    if (!next) reset();
  }

  function onSubmit(values: CreateTeamFormValues) {
    mutateWithToast(
      createMutation,
      {
        name: values.name.trim(),
        slug: values.slug ? values.slug.trim() : undefined,
        description: values.description ? values.description : null,
      },
      {
        success: 'Team created',
        error: 'Failed to create team',
      },
    );
  }

  const teams = teamsQuery.data;

  const newTeamButton = (
    <CanGuard permission="teams:manage">
      <Button size="sm" onClick={() => setIsCreateOpen(true)} aria-label="New team">
        <Plus className="icon-sm" />
        New team
      </Button>
    </CanGuard>
  );

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        iconVariant="box"
        icon={<UsersRound className="h-5 w-5 text-muted-foreground" />}
        title="Teams"
        description="Group members and grant shared access."
        docs="administration/organization-teams#teams"
        actions={<div className="hidden sm:block">{newTeamButton}</div>}
      />

      <SectionCard
        title="All teams"
        description="Teams can be referenced by integrations and routing rules."
        action={
          teams && teams.length > 0 ? <div className="sm:hidden">{newTeamButton}</div> : undefined
        }
        data-testid="teams-section"
        contentClassName="px-3 sm:px-6"
      >
        {teamsQuery.isLoading ? (
          <div className="space-y-2" data-testid="teams-loading">
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
          </div>
        ) : teams && teams.length > 0 ? (
          <ul className="divide-y divide-border overflow-hidden rounded-md border">
            {teams.map((team) => (
              <li
                key={team.id}
                className="relative transition-colors hover:bg-muted/40"
                data-testid={`team-row-${team.id}`}
              >
                <Link
                  to={routes.team(team.id)}
                  aria-label={`View team ${team.name}`}
                  className="flex min-w-0 flex-col gap-3 p-3 pr-12 active:bg-muted/60 sm:flex-row sm:items-center sm:gap-3 sm:p-4 sm:pr-14"
                >
                  <div className="flex min-w-0 items-start gap-3 sm:flex-1 sm:items-center">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-medium uppercase">
                      {team.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
                        <span className="font-medium sm:truncate">{team.name}</span>
                        <Badge variant="secondary" className="w-fit text-[10px]">
                          {team.slug}
                        </Badge>
                      </div>
                      {team.description ? (
                        <p className="mt-1 text-sm text-muted-foreground sm:mt-0.5 sm:truncate">
                          {team.description}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground/60 sm:mt-0.5">
                          No description
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-border pt-3 sm:justify-end sm:border-0 sm:pt-0">
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {team.memberCount} member{team.memberCount === 1 ? '' : 's'}
                    </span>
                    <ChevronRight className="icon-sm shrink-0 text-muted-foreground" />
                  </div>
                </Link>
                {/* Team-scoped, like `DELETE /teams/:teamId`: a maintainer of
                    this team can delete it without org-wide teams:manage. */}
                <CanGuard permission="teams:manage" team={team.id}>
                  <div className="absolute top-1.5 right-1.5 sm:top-1/2 sm:right-3 sm:-translate-y-1/2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={deleteMutation.isPending}
                          aria-label={`Delete ${team.name}`}
                        >
                          <Trash2 className="icon-sm" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete team?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {team.name} will be removed along with its {team.memberCount} member
                            {team.memberCount === 1 ? '' : 's'} and its role bindings. This cannot
                            be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            variant="destructive"
                            onClick={() =>
                              mutateWithToast(deleteMutation, team.id, {
                                success: `${team.name} deleted`,
                                error: 'Failed to delete team',
                              })
                            }
                          >
                            Delete team
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CanGuard>
              </li>
            ))}
          </ul>
        ) : (
          <div data-testid="teams-empty">
            <EmptyState
              variant="bordered"
              icon={<Users className="icon-lg" />}
              title="No teams yet"
              description="Group members together to manage shared access."
              action={newTeamButton}
            />
          </div>
        )}
      </SectionCard>

      <Dialog open={isCreateOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create team</DialogTitle>
            <DialogDescription>
              Teams group members together for shared access grants.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="team-name">Name</Label>
              <Input id="team-name" placeholder="Platform" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="team-slug">Identifier (optional)</Label>
              <Input id="team-slug" placeholder="platform" {...register('slug')} />
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="team-description">Description (optional)</Label>
              <Input
                id="team-description"
                placeholder="What this team is for"
                {...register('description')}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!isValid || createMutation.isPending}>
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
